import API from '@/apis/AppApi'
import DefaultPolicy from '@/config/passwordPolicy.default'

/**
 * Module Vuex regroupant tout ce qui concerne le mot de passe côté "état de session" :
 *  - la politique publique (`GET /api/password-policy`), utilisée pour le confort
 *    utilisateur uniquement (voir `src/utils/passwordPolicy.js` et le commentaire du
 *    fichier fallback `src/config/passwordPolicy.default.js` — le backend reste seul
 *    juge de la validité réelle) ;
 *  - le flag "mot de passe expiré / à changer", positionné soit proactivement à partir
 *    du bloc `password` renvoyé par `login`/`autologin`/`retrievelogin`
 *    (`must_change: true`), soit réactivement par l'intercepteur HTTP
 *    (`src/plugins/axios/DefaultInterceptor.js`) sur réception d'un 403
 *    `{"code": "PASSWORD_EXPIRED"}` sur n'importe quelle route authentifiée ;
 *  - le nombre de jours avant expiration (`days_before_expiration`), utilisé par
 *    `PasswordExpiryBanner.vue` pour n'afficher un avertissement non bloquant qu'à
 *    l'approche de l'expiration (comparé à `policy.expiration_warning_days`).
 *
 * Le cache localStorage n'est qu'une amélioration perçue (évite un flash de policy
 * par défaut le temps que la requête réponde) : `fetch()` interroge toujours le
 * serveur, jamais uniquement le cache.
 */

const CACHE_KEY = 'passwordPolicyCache'

function readCache() {
	try {
		const raw = localStorage.getItem(CACHE_KEY)
		return raw ? JSON.parse(raw) : null
	} catch (e) {
		return null
	}
}

function writeCache(policy) {
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify(policy))
	} catch (e) {
		// stockage indisponible (navigation privée, quota...) : sans conséquence, le
		// fallback en mémoire (state.data) reste opérant pour la session en cours.
	}
}

export default {
	namespaced: true,
	state: {
		data: null,
		source: null, // 'remote' | 'cache' | 'default'
		loading: false,
		error: null,
		mustChangePassword: false,
		expiredAt: null, // horodatage renvoyé par le backend (403.expired_at), informatif uniquement
		daysBeforeExpiration: null,
	},
	getters: {
		policy: state => state.data || DefaultPolicy,
		version: (state, getters) => getters.policy.version,
		isLoading: state => state.loading,
		hasError: state => state.error !== null,
		mustChangePassword: state => state.mustChangePassword,
		expiredAt: state => state.expiredAt,
		daysBeforeExpiration: state => state.daysBeforeExpiration,
		isExpirationWarningActive: (state, getters) => {
			const warningThreshold = getters.policy.expiration_warning_days
			return typeof state.daysBeforeExpiration === 'number' &&
				!!warningThreshold &&
				state.daysBeforeExpiration <= warningThreshold
		},
	},
	mutations: {
		setPolicy(state, { data, source }) {
			state.data = data
			state.source = source
		},
		setLoading(state, loading) {
			state.loading = loading
		},
		setError(state, error) {
			state.error = error
		},
		setMustChangePassword(state, expiredAt) {
			state.mustChangePassword = !!expiredAt || expiredAt === true
			state.expiredAt = typeof expiredAt === 'string' ? expiredAt : null
		},
		setDaysBeforeExpiration(state, days) {
			state.daysBeforeExpiration = typeof days === 'number' ? days : null
		},
		reset(state) {
			// La politique publique n'a pas besoin d'être invalidée à la déconnexion :
			// seuls les flags propres à la session utilisateur sont réinitialisés.
			state.mustChangePassword = false
			state.expiredAt = null
			state.daysBeforeExpiration = null
		},
	},
	actions: {
		fetch({ commit }) {
			commit('setLoading', true)
			return API.getPasswordPolicy()
				.then(res => res.data)
				.then(policy => {
					commit('setPolicy', { data: policy, source: 'remote' })
					commit('setError', null)
					writeCache(policy)
					return policy
				})
				.catch(error => {
					const cached = readCache()
					commit('setPolicy', { data: cached || DefaultPolicy, source: cached ? 'cache' : 'default' })
					commit('setError', error)
					return cached || DefaultPolicy
				})
				.finally(() => commit('setLoading', false))
		},
		/**
		 * Ne rafraîchit la politique que si la version connue diffère de celle passée
		 * en paramètre (ex: version reçue via un autre canal, un header de réponse...).
		 * Évite un appel réseau redondant quand la politique n'a pas changé.
		 */
		fetchIfStale({ state, dispatch }, remoteVersion) {
			if (!state.data || state.data.version !== remoteVersion) {
				return dispatch('fetch')
			}
			return Promise.resolve(state.data)
		},
		setMustChangePassword({ commit }, expiredAt) {
			commit('setMustChangePassword', expiredAt)
		},
		clearMustChangePassword({ commit }) {
			commit('setMustChangePassword', null)
		},
		setDaysBeforeExpiration({ commit }, days) {
			commit('setDaysBeforeExpiration', days)
		},
		/**
		 * Applique le bloc `password` renvoyé par les réponses de connexion
		 * (`POST /api/login`, `GET /api/autologin`, `GET /api/retrievelogin`) :
		 * `{ changed_at, expires_at, days_before_expiration, must_change }`.
		 * Permet de savoir dès la connexion qu'un changement est requis, sans
		 * attendre un premier 403 sur une requête authentifiée quelconque.
		 */
		applyPasswordBlock({ commit }, passwordBlock) {
			if (!passwordBlock) {
				return
			}
			commit('setMustChangePassword', passwordBlock.must_change ? (passwordBlock.expires_at || true) : null)
			commit(
				'setDaysBeforeExpiration',
				typeof passwordBlock.days_before_expiration === 'number' ? passwordBlock.days_before_expiration : null
			)
		},
		reset({ commit }) {
			commit('reset')
		},
	},
}
